import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useQueryParams, withDefault, StringParam } from "use-query-params";
import { useRouter } from "next/router";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";

interface SidebarHistoryEntry {
  objectId: string;
  sidebarType: DetailSidebarType;
  tableId: string;
}

export interface DetailSidebarContextValue {
  objectId: string | null;
  sidebarType: DetailSidebarType | null;
  tableId: string | null;
  canNavigateNext: boolean;
  canNavigatePrevious: boolean;
  sidebarHistory: SidebarHistoryEntry[];
  canGoBack: boolean;
  rootNavigationContext: SidebarHistoryEntry | null;
  _toggleSidebar: (
    e: React.MouseEvent<HTMLElement> | undefined,
    objectId: string,
    sidebarType: DetailSidebarType,
    tableId: string,
    isExternalCall?: boolean,
  ) => void;
  closeSidebar: () => void;
  goBack: () => void;
  setCanNavigateNext: (can: boolean) => void;
  setCanNavigatePrevious: (can: boolean) => void;
}

const DetailSidebarContext = createContext<DetailSidebarContextValue | null>(
  null,
);

interface DetailSidebarProviderProps {
  children: React.ReactNode;
}

export const DetailSidebarProvider: React.FC<DetailSidebarProviderProps> = ({
  children,
}) => {
  const router = useRouter();
  const previousPathname = useRef<string | null>(null);

  const [{ objectId, sidebarType, tableId }, setQueryParams] = useQueryParams({
    objectId: withDefault(StringParam, null),
    sidebarType: withDefault(StringParam, null),
    tableId: withDefault(StringParam, null),
    integrationTab: withDefault(StringParam, null),
  });

  const [canNavigateNext, setCanNavigateNext] = useState(false);
  const [canNavigatePrevious, setCanNavigatePrevious] = useState(false);
  const [sidebarHistory, setSidebarHistory] = useState<SidebarHistoryEntry[]>(
    [],
  );
  const [rootNavigationContext, setRootNavigationContext] =
    useState<SidebarHistoryEntry | null>(null);
  // Main sidebar control function
  const _toggleSidebar = useCallback(
    (
      e: React.MouseEvent<HTMLElement> | undefined,
      newObjectId: string,
      newSidebarType: DetailSidebarType,
      newTableId: string,
      isExternalCall: boolean = false,
    ) => {
      // Handle cmd/ctrl click to open detail page in new tab
      if (e?.metaKey || e?.ctrlKey) {
        e?.preventDefault();
        const detailPageUrl = `/detail/${newSidebarType}/${newObjectId}`;
        window.open(detailPageUrl, "_blank");
        return;
      }
      // If clicking the same item, close the sidebar
      if (
        objectId === newObjectId &&
        sidebarType === newSidebarType &&
        tableId === newTableId
      ) {
        setSidebarHistory([]); // Clear history when closing
        setQueryParams(
          {
            objectId: null,
            sidebarType: null,
            tableId: null,
            integrationTab: null,
          },
          "pushIn",
        );
      } else {
        // Push current state to history if we're currently showing a sidebar
        if (objectId && sidebarType && tableId) {
          setSidebarHistory((prev) => [
            ...prev,
            {
              objectId,
              sidebarType: sidebarType as DetailSidebarType,
              tableId,
            },
          ]);
        }
        if (isExternalCall) {
          setSidebarHistory([]);
        }
        if (isExternalCall || !rootNavigationContext) {
          // This is an external call or we don't have a root context yet
          setRootNavigationContext({
            objectId: newObjectId,
            sidebarType: newSidebarType,
            tableId: newTableId,
          });
        }
        // If isExternalCall is false and we have rootNavigationContext,
        // we preserve the existing root context (internal navigation)

        // Open sidebar with new item
        setQueryParams(
          {
            objectId: newObjectId,
            sidebarType: newSidebarType,
            tableId: newTableId,
            integrationTab: null,
          },
          "pushIn",
        );
      }
    },
    [objectId, sidebarType, tableId, setQueryParams, rootNavigationContext],
  );

  const closeSidebar = useCallback(() => {
    setSidebarHistory([]); // Clear history when closing
    setRootNavigationContext(null); // Clear root navigation context
    setQueryParams(
      {
        objectId: null,
        sidebarType: null,
        tableId: null,
        integrationTab: null,
      },
      "pushIn",
    );
  }, [setQueryParams]);

  const goBack = useCallback(() => {
    if (sidebarHistory.length === 0) return;

    const previousEntry = sidebarHistory[sidebarHistory.length - 1];
    const newHistory = sidebarHistory.slice(0, -1);

    setSidebarHistory(newHistory);
    setQueryParams(
      {
        objectId: previousEntry.objectId,
        sidebarType: previousEntry.sidebarType,
        tableId: previousEntry.tableId,
        integrationTab: null,
      },
      "pushIn",
    );
  }, [sidebarHistory, setQueryParams]);

  const canGoBack = sidebarHistory.length > 0;

  // Close sidebar when navigating to a different page
  useEffect(() => {
    if (
      previousPathname.current !== null &&
      previousPathname.current !== router.pathname
    ) {
      // Only close sidebar if we're navigating to a completely different base path
      // This prevents sidebar from closing when just query parameters change
      const currentBasePath = router.pathname.split("?")[0];
      const previousBasePath = previousPathname.current.split("?")[0];

      const hasExplicitSidebarDestination = objectId && sidebarType && tableId;

      if (
        currentBasePath !== previousBasePath &&
        !hasExplicitSidebarDestination
      ) {
        closeSidebar();
      }
    }
    previousPathname.current = router.pathname;
  }, [router.pathname, closeSidebar, objectId, sidebarType, tableId]);

  const contextValue: DetailSidebarContextValue = useMemo(
    () => ({
      _toggleSidebar,
      closeSidebar,
      goBack,
      objectId,
      sidebarType: sidebarType as DetailSidebarType | null,
      tableId,
      sidebarHistory,
      canGoBack,
      rootNavigationContext,
      setCanNavigateNext,
      setCanNavigatePrevious,
      canNavigateNext,
      canNavigatePrevious,
    }),
    [
      _toggleSidebar,
      closeSidebar,
      goBack,
      objectId,
      sidebarType,
      tableId,
      sidebarHistory,
      canGoBack,
      rootNavigationContext,
      setCanNavigateNext,
      setCanNavigatePrevious,
      canNavigateNext,
      canNavigatePrevious,
    ],
  );

  return (
    <DetailSidebarContext.Provider value={contextValue}>
      {children}
    </DetailSidebarContext.Provider>
  );
};

export const useDetailSidebarContext = (): DetailSidebarContextValue => {
  const context = useContext(DetailSidebarContext);
  if (!context) {
    throw new Error(
      "useDetailSidebarContext must be used within a DetailSidebarProvider",
    );
  }
  return context;
};
